import React, {
  forwardRef, useEffect, useImperativeHandle, useRef, useState,
} from 'react';
import styles from './pdfPreview.module.css';
import FileUtils, { PDF_PASSWORD_PROTECTED_ERROR } from '../../utils/file.utils';
import PdfPreviewError from './PdfPreviewError';
import { usePdfViewerControl } from '../../hooks/usePdfViewerControl';

export enum PdfScrollMode {
  VERTICAL = 0,
  HORIZONTAL = 1,
  WRAPPED = 2,
  PAGE = 3,
}

export enum PdfSpreadMode {
  UNKNOWN = -1,
  NONE = 0,
  ODD = 1,
  EVEN = 2,
}

interface PdfPreviewProps {
  className?: string;
  pdfFile: File | null | undefined,
  style?: React.CSSProperties,
  pageWidth?: number,
  pageHeight?: number,
  zoom?: number,
  scrollMode?: PdfScrollMode,
  spreadMode?: PdfSpreadMode,
  onClickExpandPreview?: React.MouseEventHandler;
}

const PdfPreview = forwardRef<HTMLIFrameElement, PdfPreviewProps>(({
  className, pdfFile, zoom = 1, pageHeight, pageWidth, style, scrollMode = PdfScrollMode.VERTICAL, spreadMode = PdfSpreadMode.NONE, onClickExpandPreview,
}, ref) => {
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const { passwordCancelled, resetPasswordCancelled } = usePdfViewerControl(iframeRef);

  useImperativeHandle(ref, () => iframeRef.current as HTMLIFrameElement);

  useEffect(() => {
    resetPasswordCancelled();

    if (pdfFile) {
      let cancelled = false;
      const url = URL.createObjectURL(pdfFile);
      FileUtils.validateDocumentFile(pdfFile).then((error) => {
        if (cancelled) {
          return;
        }
        const pdfFileError = FileUtils.getPdfDisplayErrorMessage(error);
        setDataUrl(pdfFileError ? null : url);
        setErrorMessage(pdfFileError);
      });

      return () => {
        cancelled = true;
        URL.revokeObjectURL(url);
      };
    }
    setDataUrl(null);
    setErrorMessage('');
    return undefined;
  }, [pdfFile, resetPasswordCancelled]);

  const containerStyle: React.CSSProperties = {
    width: pageWidth ? `${pageWidth}px` : '100%',
    height: pageHeight ? `${pageHeight}px` : '100%',
    position: 'relative',
    overflow: 'hidden',
  };

  const iframeStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    border: 'none',
    transform: `scale(${zoom})`,
    transformOrigin: 'top left',
    ...style,
  };

  useEffect(() => {
    localStorage.removeItem('pdfjs.history');

    return () => {
      localStorage.removeItem('pdfjs.history');
    };
  }, []);

  const displayErrorMessage = passwordCancelled
    ? FileUtils.getPdfDisplayErrorMessage(PDF_PASSWORD_PROTECTED_ERROR)
    : errorMessage;

  return (
    <div className={[styles.container, className].join(' ')} style={containerStyle}>
      {dataUrl && !passwordCancelled && (
      <>
        {
          onClickExpandPreview && (
            <img
              className={styles.arrowImg}
              src="/images/provisory-images/arrow-up-right.svg"
              alt=""
              onClick={onClickExpandPreview}
            />
          )
        }
        <iframe
          ref={iframeRef}
          src={`pdfjs/web/viewer.html?file=${dataUrl}&ccMode=preview&ccScrollMode=${scrollMode}&ccSpreadMode=${spreadMode}`}
          style={iframeStyle}
          title="pdf-preview"
        />
      </>
      )}
      {displayErrorMessage && <PdfPreviewError errorMessage={displayErrorMessage} />}
    </div>
  );
});

PdfPreview.displayName = 'PdfPreview';

export default PdfPreview;
